import isEmpty from 'lodash/isEmpty.js'

import {isPresent, isNumeric} from '../../../../../common/utils.js'
import apiverson from '../../../../../common/apiverson.js'
import {setColumnsToDataMap} from '../../dataImport/index.js'
import baseStrategy from './baseStrategy.js'
import {
  getWarehouseName,
  warehousesByNameSelector,
  warehousesSelector,
} from '../../../../../data/warehouses.js'

const HEADER = [
  'Reference ID *Req',
  'Warehouse *Req',
  'Internal Notes',
  'Manufacturer Notes',
  'Shipping Cost',
  'Tax Cost',
  'Line SKU *Req',
  'Line Requested Quantity *Req',
  'Line Unit Cost',
  'Line Labor Unit Cost',
  'Line Internal Notes',
  'Line Manufacturer Notes',
]

export default class moCreateStrategy extends baseStrategy {
  static title = 'Create MFG Order'
  static requiredColumns = [
    'reference_id',
    'warehouse',
    'sku',
    'requested_quantity',
  ]

  static getExampleRows() {
    return (dispatch, getState) => {
      const warehousesByID = warehousesSelector(getState())

      return apiverson
        .get('/manufacturing_order', {limit: 5})
        .then(({json: data}) => this.handleExampleRows(data, warehousesByID))
    }
  }

  static handleExampleRows(data, warehousesByID) {
    const rows = [[...HEADER]]

    function expandBaseForEachLine(row, lines) {
      return lines.map((line) => [
        ...row,
        line.sku,
        line.requested_quantity,
        line.unit_cost,
        line.labor_unit_cost,
        line.internal_notes,
        line.manufacturer_notes,
      ])
    }

    data.manufacturing_order.forEach((mo) => {
      const base = [
        mo.reference_id,
        getWarehouseName(warehousesByID[mo.warehouse_id]),
        mo.internal_notes,
        mo.manufacturer_notes,
        mo.shipping_amount,
        mo.tax_amount,
      ]

      rows.push(...expandBaseForEachLine(base, mo.lines))
    })

    return rows
  }

  static guessAtColumnDataMap(columns) {
    function find(regex) {
      return columns.findIndex((column) => column.search(regex) !== -1)
    }

    const columnsToDataMap = {
      reference_id: find(/reference.id/i),
      warehouse: find(/warehouse/i),
      internal_notes: find(/^internal.notes$/i),
      manufacturer_notes: find(/^manufacturer.notes$/i),
      shipping_amount: find(/^shipping.(amount|cost(s)?)$/i),
      tax_amount: find(/^tax.(amount|cost(s)?)$/i),
      line_sku: find(/sku/i),
      line_requested_quantity: find(/requested.quantity/i),
      line_unit_cost: find(/^(line.)?unit.cost(s)?$/i),
      line_labor_unit_cost: find(/^(line.)?labor.unit.cost(s)?$/i),
      line_internal_notes: find(/^line.internal.notes$/i),
      line_manufacturer_notes: find(/^line.manufacturer.notes$/i),
    }

    return setColumnsToDataMap(columnsToDataMap)
  }

  static transformRowsToData(rows, columnsToDataMap) {
    return (dispatch, getState) => {
      const warehousesByName = warehousesByNameSelector(getState())
      const dataByID = {}

      for (let i = 0; i < rows.length; i++) {
        const data = this.transformRowToData(
          rows[i],
          columnsToDataMap,
          warehousesByName,
        )
        const reference_id = data.payload.reference_id

        dataByID[reference_id] = dataByID[reference_id] || []

        dataByID[reference_id].push(data)
      }

      return Object.keys(dataByID).map((orderID, index) => {
        const dataSet = dataByID[orderID]
        const data = dataSet[0]
        data.index = index

        data.payload.lines = dataSet.map(({payload}) => payload.lines[0])

        data.errors = this.determineDataErrors(data)

        return data
      })
    }
  }

  static transformRowToData(row, columnsByName, warehousesByName) {
    const data = this.getNewData()
    const warehouse = warehousesByName[row[columnsByName.warehouse]] || {
      id: '-1',
    }

    data.payload = {
      reference_id: row[columnsByName.reference_id],
      warehouse_id: warehouse.id,
      internal_notes: row[columnsByName.internal_notes],
      manufacturer_notes: row[columnsByName.manufacturer_notes],
      shipping_amount: row[columnsByName.shipping_amount],
      tax_amount: row[columnsByName.tax_amount],
      lines: [
        {
          sku: row[columnsByName.line_sku],
          requested_quantity: row[columnsByName.line_requested_quantity],
          unit_cost: row[columnsByName.line_unit_cost],
          labor_unit_cost: row[columnsByName.line_labor_unit_cost],
          internal_notes: row[columnsByName.line_internal_notes],
          manufacturer_notes: row[columnsByName.line_manufacturer_notes],
        },
      ],
    }

    return data
  }

  static determineDataErrors(data) {
    const payload = data.payload
    const errors = {}

    if (data.errors.server) {
      errors.server = data.errors.server
    }

    this.required(payload, errors, 'reference_id', 'Reference ID is required')

    if (
      isPresent(payload.shipping_amount) &&
      !isNumeric(payload.shipping_amount)
    ) {
      errors.shipping_amount = 'Shipping Cost must be a number'
    }

    if (isPresent(payload.tax_amount) && !isNumeric(payload.tax_amount)) {
      errors.tax_amount = 'Tax Cost must be a number'
    }

    if (payload.warehouse_id === '-1') {
      errors.warehouse_id = 'Select a warehouse'
    } else {
      delete errors.warehouse_id
    }

    const lineErrors = payload.lines.reduce((_lineErrors, line, index) => {
      const errs = {}

      if (!isPresent(line.sku)) {
        errs.sku = 'SKU is required'
      }

      if (!isNumeric(line.requested_quantity)) {
        errs.requested_quantity = 'Quantity must be a number'
      }

      if (isPresent(line.unit_cost) && !isNumeric(line.unit_cost)) {
        errs.unit_cost = 'Unit Cost must be a number'
      }

      if (isPresent(line.labor_unit_cost) && !isNumeric(line.labor_unit_cost)) {
        errs.labor_unit_cost = 'Labor Unit Cost must be a number'
      }

      if (!isEmpty(errs)) {
        _lineErrors[index] = errs
      }

      return _lineErrors
    }, {})

    if (!isEmpty(lineErrors)) {
      errors.lines = lineErrors
    } else {
      delete errors.lines
    }

    return errors
  }

  static save(data) {
    return async () => {
      const mo = {
        reference_id: data.payload.reference_id,
        warehouse_id: Number(data.payload.warehouse_id),
      }

      if (data.payload.internal_notes) {
        mo.internal_notes = data.payload.internal_notes
      }

      if (data.payload.manufacturer_notes) {
        mo.manufacturer_notes = data.payload.manufacturer_notes
      }

      if (data.payload.shipping_amount) {
        mo.shipping_amount = Number(data.payload.shipping_amount)
      }

      if (data.payload.tax_amount) {
        mo.tax_amount = Number(data.payload.tax_amount)
      }

      mo.lines = data.payload.lines.map((line) => {
        const newLine = {
          sku: line.sku,
          requested_quantity: Number(line.requested_quantity),
        }

        if (line.unit_cost) {
          newLine.unit_cost = Number(line.unit_cost)
        }

        if (line.labor_unit_cost) {
          newLine.labor_unit_cost = Number(line.labor_unit_cost)
        }

        if (line.internal_notes) {
          newLine.internal_notes = line.internal_notes
        }

        if (line.manufacturer_notes) {
          newLine.manufacturer_notes = line.manufacturer_notes
        }

        return newLine
      })

      return await apiverson.post('/manufacturing_order', mo)
    }
  }
}
